import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Select, MenuItem, FormControl, InputLabel, Chip, Stack, Alert, IconButton, Tooltip, CircularProgress, Autocomplete } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import TableChartIcon from "@mui/icons-material/TableChart";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FilePreviewModal from "./FilePreviewModal";
import { allSections, pageLabels } from "@/components/sections/registry";

// ── Priority config ──

const PRIORITY_OPTIONS = [{
  value: 0,
  label: "P0 — Critical",
  color: "error",
  sx: {
    bgcolor: "#7f1d1d",
    color: "#fff"
  }
}, {
  value: 1,
  label: "P1 — High",
  color: "error"
}, {
  value: 2,
  label: "P2 — Medium",
  color: "warning"
}, {
  value: 3,
  label: "P3 — Low",
  color: "success"
}];

// ── Unique page paths from registry ──

function getUniquePages() {
  const set = new Set();
  for (const s of allSections) {
    for (const p of s.pagePatterns) {
      set.add(p);
    }
  }
  const pages = Array.from(set).sort();
  // Move "*" to end if present
  const idx = pages.indexOf("*");
  if (idx >= 0) {
    pages.splice(idx, 1);
    pages.push("*");
  }
  return pages;
}
const ALL_PAGES = getUniquePages();
function getSectionsForPage(pagePath) {
  if (pagePath === "*") {
    // "All Pages" → only show shared (global) sections
    return allSections.filter(s => s.pagePatterns.includes("*"));
  }
  // Specific page → show page-specific sections only, NOT shared ones
  return allSections.filter(s => !s.pagePatterns.includes("*") && s.pagePatterns.includes(pagePath));
}

// ── TSV → Markdown table conversion (shared utility) ──
import { tsvToMarkdown, looksLikeTsv } from "@/utils/tsvTable";

// ── Types ──
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ── Component ──

export default function TaskModal({
  open,
  onClose,
  mode,
  initialData,
  onSave,
  saving = false,
  error = null,
  onUploadScreenshot,
  onRemoveScreenshot,
  onUploadFile,
  onRemoveAttachment,
  onDelete
}) {
  // ── State ──
  const [pagePath, setPagePath] = useState(initialData?.pagePath || "");
  const [sectionLabel, setSectionLabel] = useState(initialData?.sectionLabel || "");
  const [priority, setPriority] = useState(initialData?.priority ?? 2);
  const [body, setBody] = useState(initialData?.body || "");
  const [tableInserted, setTableInserted] = useState(false);
  const [screenshots, setScreenshots] = useState(initialData?.screenshots || []);
  const [ssUploading, setSsUploading] = useState(false);
  const [ssError, setSsError] = useState(null);
  const [ssPreviewUrl, setSsPreviewUrl] = useState(null);
  const ssFileRef = useRef(null);
  const messageRef = useRef(null);
  const [attachments, setAttachments] = useState(initialData?.attachments || []);
  const [deleting, setDeleting] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const fileRef = useRef(null);
  const [subtasks, setSubtasks] = useState(initialData?.subtasks || []);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskText, setEditingSubtaskText] = useState("");
  const [attempted, setAttempted] = useState(false);

  // Derived section list
  const availableSections = useMemo(() => getSectionsForPage(pagePath), [pagePath]);

  // Reset on open/initialData change
  useEffect(() => {
    if (open) {
      setPagePath(initialData?.pagePath || "");
      setSectionLabel(initialData?.sectionLabel || "");
      setPriority(initialData?.priority ?? 2);
      setBody(initialData?.body || "");
      setScreenshots(initialData?.screenshots || []);
      setAttachments(initialData?.attachments || []);
      setSubtasks(initialData?.subtasks || []);
      setEditingSubtaskId(null);
      setEditingSubtaskText("");
      setTableInserted(false);
      setSsError(null);
      setSsPreviewUrl(null);
      setFileError(null);
      setPreviewFile(null);
      setAttempted(false);
    }
  }, [open, initialData]);

  // Auto-set elementSelector from sectionLabel
  const elementSelector = sectionLabel ? `[data-select-target="${sectionLabel}"]` : "";

  // ── Paste handler: detect TSV and convert to markdown ──
  const handlePaste = useCallback(e => {
    const text = e.clipboardData.getData("text/plain");
    if (looksLikeTsv(text)) {
      e.preventDefault();
      const md = tsvToMarkdown(text);
      // Insert at cursor or append
      const textarea = messageRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = body.slice(0, start);
        const after = body.slice(end);
        const nl = before.length > 0 && !before.endsWith("\n") ? "\n" : "";
        setBody(before + nl + md + "\n" + after);
      } else {
        setBody(prev => prev ? prev + "\n" + md : md);
      }
      setTableInserted(true);
    }
  }, [body]);

  // ── Screenshot upload ──
  const handleScreenshotUpload = useCallback(async files => {
    if (!files || !onUploadScreenshot) return;
    const batch = Array.from(files).slice(0, 5 - (screenshots?.length || 0));
    if (batch.length === 0) return;
    setSsUploading(true);
    setSsError(null);
    const newSs = [...(screenshots || [])];
    for (const file of batch) {
      try {
        const result = await onUploadScreenshot(file);
        newSs.push({
          id: `ss_${Math.random().toString(36).slice(2, 10)}`,
          ...result
        });
      } catch (err) {
        setSsError(err?.message || "Upload failed");
      }
    }
    setScreenshots(newSs);
    setSsUploading(false);
    if (ssFileRef.current) ssFileRef.current.value = "";
  }, [screenshots, onUploadScreenshot]);

  // ── File upload ──
  const handleFileUpload = useCallback(async files => {
    if (!files || !onUploadFile) return;
    const batch = Array.from(files).slice(0, 10 - (attachments?.length || 0));
    if (batch.length === 0) return;
    setFileUploading(true);
    setFileError(null);
    const newAtts = [...(attachments || [])];
    for (const file of batch) {
      try {
        const result = await onUploadFile(file);
        newAtts.push({
          id: `att_${Math.random().toString(36).slice(2, 10)}`,
          ...result
        });
      } catch (err) {
        setFileError(err?.message || "Upload failed");
      }
    }
    setAttachments(newAtts);
    setFileUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  }, [attachments, onUploadFile]);

  // ── Validation ──
  const canSave = pagePath.trim().length > 0 && sectionLabel.trim().length > 0 && (mode === "edit" || body.trim().length > 0);

  // ── Save ──
  const handleSave = () => {
    setAttempted(true);
    if (!canSave || saving) return;
    onSave({
      id: initialData?.id,
      pagePath,
      sectionLabel,
      elementSelector,
      elementText: initialData?.elementText,
      priority,
      body,
      screenshots,
      attachments,
      subtasks
    });
  };
  const title = mode === "create" ? "Create Task" : mode === "capture" ? "Capture Task" : "Edit Task";
  const isPageLocked = mode === "capture";
  return /*#__PURE__*/_jsxs(Dialog, {
    open: open,
    onClose: saving ? undefined : onClose,
    maxWidth: "sm",
    fullWidth: true,
    children: [/*#__PURE__*/_jsx(DialogTitle, {
      children: title
    }), /*#__PURE__*/_jsxs(DialogContent, {
      dividers: true,
      children: [/*#__PURE__*/_jsxs(Stack, {
        spacing: 2,
        sx: {
          mt: 0.5
        },
        children: [error && /*#__PURE__*/_jsx(Alert, {
          severity: "error",
          children: error
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            gap: 1.5
          },
          children: [isPageLocked ? /*#__PURE__*/_jsx(TextField, {
            size: "small",
            label: "Page",
            value: pageLabels[pagePath] || pagePath || "",
            sx: {
              flex: 2
            },
            InputProps: {
              readOnly: true
            }
          }) : /*#__PURE__*/_jsxs(FormControl, {
            size: "small",
            sx: {
              flex: 2
            },
            error: attempted && !pagePath.trim(),
            children: [/*#__PURE__*/_jsx(InputLabel, {
              id: "task-page-label",
              children: "Page"
            }), /*#__PURE__*/_jsx(Select, {
              labelId: "task-page-label",
              value: pagePath,
              label: "Page",
              onChange: e => {
                setPagePath(e.target.value);
                setSectionLabel("");
              },
              children: ALL_PAGES.map(p => /*#__PURE__*/_jsx(MenuItem, {
                value: p,
                children: pageLabels[p] || (p === "*" ? "All Pages" : p)
              }, p))
            })]
          }), /*#__PURE__*/_jsx(Autocomplete, {
            freeSolo: true,
            size: "small",
            sx: {
              flex: 2
            },
            disabled: !pagePath && !isPageLocked,
            options: availableSections.map(s => s.selectTarget),
            value: sectionLabel,
            onChange: (_e, newValue) => setSectionLabel(newValue ?? ""),
            onInputChange: (_e, newInput, reason) => {
              if (reason !== "reset") setSectionLabel(newInput);
            },
            renderInput: params => /*#__PURE__*/_jsx(TextField, {
              ...params,
              label: "Section",
              error: attempted && !sectionLabel.trim()
            })
          }), /*#__PURE__*/_jsxs(FormControl, {
            size: "small",
            sx: {
              flex: 1.5,
              // Keep the outlined border height in lock-step
              // with the sibling Page/Section inputs by
              // overriding the Chip-driven minHeight bump.
              "& .MuiSelect-select": {
                minHeight: 0,
                display: "flex",
                alignItems: "center"
              }
            },
            children: [/*#__PURE__*/_jsx(InputLabel, {
              id: "task-priority-label",
              children: "Priority"
            }), /*#__PURE__*/_jsx(Select, {
              labelId: "task-priority-label",
              value: priority,
              label: "Priority",
              onChange: e => setPriority(e.target.value),
              renderValue: val => {
                const opt = PRIORITY_OPTIONS.find(o => o.value === val);
                return opt ? /*#__PURE__*/_jsx(Chip, {
                  label: opt.label,
                  color: opt.color,
                  size: "small",
                  sx: {
                    ...opt.sx,
                    fontWeight: 700,
                    height: 20
                  }
                }) : null;
              },
              children: PRIORITY_OPTIONS.map(o => /*#__PURE__*/_jsx(MenuItem, {
                value: o.value,
                children: /*#__PURE__*/_jsx(Chip, {
                  label: o.label,
                  color: o.color,
                  size: "small",
                  variant: "filled",
                  sx: {
                    ...o.sx,
                    fontWeight: 700
                  }
                })
              }, o.value))
            })]
          })]
        }), /*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsx(TextField, {
            inputRef: messageRef,
            label: mode === "edit" ? "Update description" : "Task Description",
            multiline: true,
            minRows: 3,
            maxRows: 10,
            fullWidth: true,
            value: body,
            onChange: e => setBody(e.target.value),
            onPaste: handlePaste,
            error: attempted && mode !== "edit" && !body.trim(),
            placeholder: "Describe the task, or paste a table from a spreadsheet\u2026",
            helperText: tableInserted ? "Table pasted as markdown" : "Paste from Excel/Sheets to insert a markdown table"
          }), tableInserted && /*#__PURE__*/_jsx(Tooltip, {
            title: "Table was converted from clipboard",
            children: /*#__PURE__*/_jsx(TableChartIcon, {
              color: "success",
              fontSize: "small",
              sx: {
                mt: 0.5
              }
            })
          })]
        }), /*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "subtitle2",
              color: "text.secondary",
              children: "Sub Tasks"
            }), /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              onClick: () => {
                const newId = `st_${Math.random().toString(36).slice(2, 10)}`;
                setSubtasks(prev => [...prev, {
                  id: newId,
                  text: "",
                  completed: false
                }]);
                setEditingSubtaskId(newId);
                setEditingSubtaskText("");
              },
              sx: {
                color: "primary.main",
                p: 0.25
              },
              children: /*#__PURE__*/_jsx(AddCircleOutlineIcon, {
                fontSize: "small"
              })
            })]
          }), subtasks.map(st => /*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
              "&:hover .st-delete": {
                opacity: 1
              }
            },
            children: [/*#__PURE__*/_jsx(IconButton, {
              size: "small",
              onClick: () => setSubtasks(prev => prev.map(s => s.id === st.id ? {
                ...s,
                completed: !s.completed
              } : s)),
              sx: {
                p: 0.25
              },
              children: st.completed ? /*#__PURE__*/_jsx(CheckCircleIcon, {
                fontSize: "small",
                color: "success"
              }) : /*#__PURE__*/_jsx(RadioButtonUncheckedIcon, {
                fontSize: "small"
              })
            }), editingSubtaskId === st.id ? /*#__PURE__*/_jsx(TextField, {
              size: "small",
              fullWidth: true,
              autoFocus: true,
              value: editingSubtaskText,
              onChange: e => setEditingSubtaskText(e.target.value),
              onBlur: () => {
                const trimmed = editingSubtaskText.trim();
                if (trimmed) {
                  setSubtasks(prev => prev.map(s => s.id === st.id ? {
                    ...s,
                    text: trimmed
                  } : s));
                } else {
                  setSubtasks(prev => prev.filter(s => s.id !== st.id));
                }
                setEditingSubtaskId(null);
                setEditingSubtaskText("");
              },
              onKeyDown: e => {
                if (e.key === "Enter") {
                  e.target.blur();
                }
              },
              placeholder: "Enter subtask\u2026",
              variant: "standard",
              sx: {
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  py: 0.25
                }
              }
            }) : /*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              sx: {
                flex: 1,
                textDecoration: st.completed ? "line-through" : "none",
                color: st.completed ? "text.secondary" : "text.primary",
                cursor: "pointer",
                fontSize: "0.875rem"
              },
              onClick: () => {
                setEditingSubtaskId(st.id);
                setEditingSubtaskText(st.text);
              },
              children: st.text
            }), editingSubtaskId !== st.id && /*#__PURE__*/_jsx(IconButton, {
              className: "st-delete",
              size: "small",
              onClick: () => setSubtasks(prev => prev.filter(s => s.id !== st.id)),
              sx: {
                opacity: 0,
                transition: "opacity 0.15s",
                p: 0.25,
                color: "text.secondary"
              },
              children: /*#__PURE__*/_jsx(CloseIcon, {
                fontSize: "small"
              })
            })]
          }, st.id))]
        }), onUploadScreenshot && /*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsxs(Typography, {
            variant: "caption",
            color: "text.secondary",
            sx: {
              mb: 0.5,
              display: "block"
            },
            children: ["Screenshots (", screenshots?.length || 0, "/5)"]
          }), /*#__PURE__*/_jsx("input", {
            ref: ssFileRef,
            type: "file",
            accept: "image/*",
            multiple: true,
            style: {
              display: "none"
            },
            onChange: e => handleScreenshotUpload(e.target.files)
          }), /*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              flexWrap: "wrap",
              gap: 1
            },
            children: [(screenshots || []).map((ss, i) => /*#__PURE__*/_jsxs(Box, {
              sx: {
                position: "relative",
                width: 100,
                height: 70,
                borderRadius: 1,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
                cursor: "pointer",
                "&:hover .ss-rm": {
                  opacity: 1
                }
              },
              onClick: () => setSsPreviewUrl(ss.originalUrl),
              children: [/*#__PURE__*/_jsx("img", {
                src: ss.thumbnailUrl,
                alt: "Screenshot",
                style: {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }
              }), onRemoveScreenshot && ss.id && /*#__PURE__*/_jsx(IconButton, {
                className: "ss-rm",
                size: "small",
                sx: {
                  position: "absolute",
                  top: 2,
                  right: 2,
                  bgcolor: "rgba(0,0,0,0.6)",
                  color: "white",
                  opacity: 0,
                  transition: "opacity 0.15s",
                  p: 0.25
                },
                onClick: e => {
                  e.stopPropagation();
                  onRemoveScreenshot(ss.id);
                  setScreenshots(prev => prev?.filter(s => s.id !== ss.id) || []);
                },
                children: /*#__PURE__*/_jsx(CloseIcon, {
                  sx: {
                    fontSize: 14
                  }
                })
              })]
            }, ss.id || i)), (screenshots?.length || 0) < 5 && /*#__PURE__*/_jsx(Box, {
              onClick: () => {
                setSsError(null);
                ssFileRef.current?.click();
              },
              sx: {
                width: 100,
                height: 70,
                border: "2px dashed",
                borderColor: ssError ? "error.main" : "grey.400",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: ssError ? "error.dark" : "primary.main",
                  bgcolor: "action.hover"
                }
              },
              children: ssUploading ? /*#__PURE__*/_jsx(CircularProgress, {
                size: 18
              }) : /*#__PURE__*/_jsx(AddPhotoAlternateIcon, {
                sx: {
                  color: ssError ? "error.main" : "text.secondary"
                }
              })
            })]
          }), ssError && /*#__PURE__*/_jsx(Typography, {
            variant: "caption",
            color: "error",
            sx: {
              mt: 0.5
            },
            children: ssError
          })]
        }), onUploadFile && /*#__PURE__*/_jsxs(Box, {
          children: [/*#__PURE__*/_jsxs(Typography, {
            variant: "caption",
            color: "text.secondary",
            sx: {
              mb: 0.5,
              display: "block"
            },
            children: ["Attachments (", attachments?.length || 0, "/10)"]
          }), /*#__PURE__*/_jsx("input", {
            ref: fileRef,
            type: "file",
            accept: ".pdf,.xlsx,.xls,.docx,.doc,.csv,image/*",
            multiple: true,
            style: {
              display: "none"
            },
            onChange: e => handleFileUpload(e.target.files)
          }), (attachments || []).length > 0 && /*#__PURE__*/_jsx(Stack, {
            spacing: 0.5,
            sx: {
              mb: 1
            },
            children: (attachments || []).map((att, i) => /*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1
              },
              children: [/*#__PURE__*/_jsx(InsertDriveFileIcon, {
                fontSize: "small",
                color: "action"
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "body2",
                sx: {
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                },
                onClick: () => setPreviewFile({
                  url: att.url,
                  filename: att.filename,
                  mimeType: att.mimeType
                }),
                children: att.filename
              }), /*#__PURE__*/_jsxs(Typography, {
                variant: "caption",
                color: "text.secondary",
                children: [(att.size / 1024).toFixed(0), " ", "KB"]
              }), onRemoveAttachment && att.id && /*#__PURE__*/_jsx(IconButton, {
                size: "small",
                onClick: () => {
                  onRemoveAttachment(att.id);
                  setAttachments(prev => prev?.filter(a => a.id !== att.id) || []);
                },
                sx: {
                  p: 0.25
                },
                children: /*#__PURE__*/_jsx(CloseIcon, {
                  sx: {
                    fontSize: 14
                  }
                })
              })]
            }, att.id || i))
          }), (attachments?.length || 0) < 10 && /*#__PURE__*/_jsx(Button, {
            variant: "outlined",
            size: "small",
            startIcon: fileUploading ? /*#__PURE__*/_jsx(CircularProgress, {
              size: 16
            }) : /*#__PURE__*/_jsx(AttachFileIcon, {}),
            disabled: fileUploading,
            onClick: () => {
              setFileError(null);
              fileRef.current?.click();
            },
            children: "Attach File"
          }), fileError && /*#__PURE__*/_jsx(Typography, {
            variant: "caption",
            color: "error",
            sx: {
              mt: 0.5,
              display: "block"
            },
            children: fileError
          })]
        })]
      }), previewFile && /*#__PURE__*/_jsx(FilePreviewModal, {
        open: true,
        onClose: () => setPreviewFile(null),
        url: previewFile.url,
        filename: previewFile.filename,
        mimeType: previewFile.mimeType
      }), ssPreviewUrl && /*#__PURE__*/_jsx(Dialog, {
        open: true,
        onClose: () => setSsPreviewUrl(null),
        maxWidth: "lg",
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "relative",
            bgcolor: "black",
            display: "flex",
            justifyContent: "center"
          },
          children: [/*#__PURE__*/_jsx(IconButton, {
            onClick: () => setSsPreviewUrl(null),
            sx: {
              position: "absolute",
              top: 8,
              right: 8,
              color: "white"
            },
            children: /*#__PURE__*/_jsx(CloseIcon, {})
          }), /*#__PURE__*/_jsx("img", {
            src: ssPreviewUrl,
            alt: "Screenshot preview",
            style: {
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain"
            }
          })]
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogActions, {
      children: [onDelete && /*#__PURE__*/_jsx(Button, {
        color: "error",
        disabled: saving || deleting,
        onClick: async () => {
          if (!window.confirm("Delete this task? This cannot be undone.")) return;
          setDeleting(true);
          try {
            await onDelete();
          } finally {
            setDeleting(false);
          }
        },
        sx: {
          mr: "auto"
        },
        children: deleting ? /*#__PURE__*/_jsx(CircularProgress, {
          size: 20,
          color: "inherit"
        }) : "Delete"
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        disabled: saving || deleting,
        children: "Cancel"
      }), /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        onClick: handleSave,
        disabled: !canSave || saving,
        children: saving ? /*#__PURE__*/_jsx(CircularProgress, {
          size: 20,
          color: "inherit"
        }) : mode === "edit" ? "Save" : "Create"
      })]
    })]
  });
}